import { lazy, Suspense } from 'react';
import { createBrowserRouter, Link, Navigate } from 'react-router-dom';
import { AdminGuard, CustomerGuard, GuestGuard } from '@/features/auth/guards';
import { FullPageLoader } from '@/shared/ui/Feedback';

const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/features/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/ForgotPasswordPage'));
const VerifyPage = lazy(() => import('@/features/auth/VerifyPage'));
const CompleteProfilePage = lazy(() => import('@/features/auth/CompleteProfilePage'));
const CustomerShell = lazy(() => import('@/features/loyalty/CustomerShell'));
const HomePage = lazy(() => import('@/features/loyalty/HomePage'));
const SpinPage = lazy(() => import('@/features/loyalty/SpinPage'));
const RewardsPage = lazy(() => import('@/features/loyalty/RewardsPage'));
const WalletPage = lazy(() => import('@/features/loyalty/WalletPage'));
const HistoryPage = lazy(() => import('@/features/loyalty/HistoryPage'));
const ProfilePage = lazy(() => import('@/features/loyalty/ProfilePage'));
const OperationsApp = lazy(() => import('@/features/operations/OperationsApp'));
const AdminLoyaltyApp = lazy(() => import('@/features/admin-loyalty/AdminLoyaltyApp'));

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<FullPageLoader />}>{children}</Suspense>;
}

function NotFound() {
  return (
    <main className="center-page">
      <span className="status-icon">
        <i className="material-icons-round">explore_off</i>
      </span>
      <h1>Không tìm thấy trang</h1>
      <p className="muted">Đường dẫn này không tồn tại hoặc đã được di chuyển.</p>
      <Link className="button button-primary" to="/">
        Về trang chủ
      </Link>
    </main>
  );
}

export const router = createBrowserRouter([
  {
    element: <GuestGuard />,
    children: [
      {
        path: '/login',
        element: (
          <Lazy>
            <LoginPage />
          </Lazy>
        ),
      },
      {
        path: '/register',
        element: (
          <Lazy>
            <RegisterPage />
          </Lazy>
        ),
      },
      {
        path: '/forgot-password',
        element: (
          <Lazy>
            <ForgotPasswordPage />
          </Lazy>
        ),
      },
      {
        path: '/verify-registration',
        element: (
          <Lazy>
            <VerifyPage />
          </Lazy>
        ),
      },
      { path: '/verify', element: <Navigate to="/login" replace /> },
    ],
  },
  {
    element: <CustomerGuard />,
    children: [
      {
        path: '/complete-profile',
        element: (
          <Lazy>
            <CompleteProfilePage />
          </Lazy>
        ),
      },
      {
        element: (
          <Lazy>
            <CustomerShell />
          </Lazy>
        ),
        children: [
          {
            index: true,
            element: (
              <Lazy>
                <HomePage />
              </Lazy>
            ),
          },
          {
            path: '/spin',
            element: (
              <Lazy>
                <SpinPage />
              </Lazy>
            ),
          },
          {
            path: '/rewards',
            element: (
              <Lazy>
                <RewardsPage />
              </Lazy>
            ),
          },
          {
            path: '/wallet',
            element: (
              <Lazy>
                <WalletPage />
              </Lazy>
            ),
          },
          {
            path: '/history',
            element: (
              <Lazy>
                <HistoryPage />
              </Lazy>
            ),
          },
          {
            path: '/profile',
            element: (
              <Lazy>
                <ProfilePage />
              </Lazy>
            ),
          },
        ],
      },
    ],
  },
  {
    element: <AdminGuard />,
    children: [
      {
        path: '/admin',
        element: (
          <Lazy>
            <OperationsApp />
          </Lazy>
        ),
      },
      {
        path: '/admin/loyalty',
        element: (
          <Lazy>
            <AdminLoyaltyApp />
          </Lazy>
        ),
      },
    ],
  },
  { path: '/home', element: <Navigate to="/" replace /> },
  { path: '*', element: <NotFound /> },
]);
